import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { AutoCompletes, DateInput, TextFields } from "./Custom/custominput";
import { format } from "date-fns";
import { UploadButton } from "./Custom/Button";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import Text from "./Custom/text";
import axiosWithToken from "./axiosTokenConfig";
import { BASE_URL, DOMAIN } from "../../global";
import MultiValueInputModal from "./multiValueInputModal";
import { fetchByTable, fetchQuery } from "./fetched";

const CommonForm = ({
  inputStates,
  updateInputObject,
  inputFields,
  colSpan,
  size,
}) => {
  const [fetchedOptions, setFetchedOptions] = useState({});
  const [inputErrors, setInputErrors] = useState({});
  const [clientAddresses, setClientAddresses] = useState([]);

  useEffect(() => {
    const fetchOptions = async (tableName, optionsFilter) => {
      try {
        const response = await fetchQuery(
          `select * from ${tableName} ${optionsFilter} `,
        );
        setFetchedOptions((prevOptions) => ({
          ...prevOptions,
          [tableName]: response,
        }));
      } catch (error) {
        toast.error("Something Went Wrong!");
      }
    };

    inputFields.forEach(({ type, options, optionsFilter }) => {
      if (
        type === "Autocomplete" &&
        options &&
        !Array.isArray(options) &&
        !fetchedOptions[options]
      ) {
        fetchOptions(options, optionsFilter ? optionsFilter : "");
      }
    });
  }, [inputFields]);

  // Fetch addresses when client changes
  useEffect(() => {
    const fetchClientAddresses = async () => {
      if (inputStates.clientId) {
        try {
          // Find selected client
          const clientOptions = fetchedOptions["clientMaster"] || [];
          const selectedClient = clientOptions.find(
            (client) => client.id === inputStates.clientId,
          );

          if (selectedClient && selectedClient.address) {
            // Parse address IDs (comma-separated)
            const addressIds = selectedClient.address
              .split(",")
              .map((id) => id.trim());
            // Fetch all addresses
            const allAddresses = await fetchQuery(`SELECT * FROM addresses `);
            // Filter addresses based on client's address IDs
            const filteredAddresses = allAddresses.filter((address) =>
              addressIds.includes(address.id.toString()),
            );
            setClientAddresses(filteredAddresses);

            // Auto-select if only one address
            if (filteredAddresses.length === 1) {
              updateInputObject("addressId", filteredAddresses[0].id);
            } else if (filteredAddresses.length === 0) {
              updateInputObject("addressId", "");
            }
          } else {
            setClientAddresses([]);
            updateInputObject("addressId", "");
          }
        } catch (error) {
          console.error("Error fetching client addresses:", error);
          setClientAddresses([]);
          updateInputObject("addressId", "");
        }
      } else {
        setClientAddresses([]);
        // Clear address selection when client is cleared
        if (inputStates.addressId) {
          updateInputObject("addressId", "");
        }
      }
    };

    fetchClientAddresses();
  }, [inputStates.clientId, fetchedOptions]);

  const handleFileUpload12 = (event, stateKey, fileType) => {
    const file = event.target.files[0];

    if (file && /[&#$*]/.test(file.name)) {
      toast.warning(
        "File name contains special characters. Please rename the file and upload again.",
      );
      return;
    }

    if (fileType && fileType.length > 0) {
      const extensionMap = {
        pdf: ["pdf"],
        excel: ["xls", "xlsx"],
        image: ["jpg", "jpeg", "png", "gif"],
        word: ["doc", "docx"],
      };

      const allowedExtensions = fileType.flatMap(
        (type) => extensionMap[type] || [],
      );
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(`Invalid file type. Allowed types: ${fileType.join(", ")}`);
        return;
      }
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error("File size exceeds 3 MB, cannot upload!");
      return;
    }

    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status == 200) {
          updateInputObject(stateKey, res.data);
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const renderInputField = (fieldConfig, index) => {
    const {
      type,
      label,
      tooltipLabel,
      downloadFile,
      stateKey,
      valueKey,
      options,
      getOptionLabelKey,
      disabled = false,
      required = false,
      disablePastDates = false,
      minimumDate,
      disableFutureDates,
      fileType = [],
    } = fieldConfig;

    const selectedValue =
      inputStates[stateKey] !== undefined && inputStates[stateKey] !== null
        ? inputStates[stateKey]
        : "";

    switch (type) {
      case "Autocomplete":
        const fullOptions = Array.isArray(options) ? options : [];
        const currentValue =
          fullOptions.find((item) => item[valueKey] === selectedValue) || null;

        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              size="small"
              id={`autocomplete-${stateKey}-${index}`}
              options={
                Array.isArray(options) ? options : fetchedOptions[options] || []
              }
              getOptionLabel={(option) =>
                options === "employees"
                  ? `${option["employeesCode"] || ""} - ${
                      option[getOptionLabelKey] || ""
                    }`
                  : option[getOptionLabelKey] || ""
              }
              value={
                (Array.isArray(options)
                  ? options
                  : fetchedOptions[options] || []
                ).find((item) => item[valueKey] == selectedValue) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={label}
                  required={required}
                />
              )}
              onChange={(event, value) => {
                updateInputObject(stateKey, value ? value[valueKey] : "");
              }}
              disabled={disabled}
            />
          </div>
        );

      case "ClientAddress":
        // If only one address available, show as disabled text field
        if (clientAddresses.length === 1) {
          const singleAddress = clientAddresses[0];
          return (
            <div key={index} className="w-full">
              <TextFields
                {...(size === "small" && {
                  className: "Listinputcss",
                  size: "small",
                })}
                id={`client-address-text-${stateKey}-${index}`}
                label={label}
                value={singleAddress[getOptionLabelKey] || ""}
                disabled={true}
              />
            </div>
          );
        }

        // If multiple addresses or none, show autocomplete dropdown
        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              size="small"
              id={`client-address-${stateKey}-${index}`}
              options={clientAddresses}
              getOptionLabel={(option) => option[getOptionLabelKey] || ""}
              value={
                clientAddresses.find(
                  (item) => item[valueKey] == selectedValue,
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth label={label} />
              )}
              onChange={(event, value) => {
                updateInputObject(stateKey, value ? value[valueKey] : "");
              }}
              disabled={
                disabled ||
                !inputStates.clientId ||
                clientAddresses.length === 0
              }
            />
          </div>
        );
      case "TextField":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`text-field-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value;
                if (fieldConfig.inputType === "number") {
                  // Accept only digits
                  if (/^\d*\.?\d*$/.test(val)) {
                    updateInputObject(stateKey, val);
                  }
                } else {
                  updateInputObject(stateKey, val);
                }
              }}
              disabled={disabled}
            />
          </div>
        );

      case "Heading":
        return (
          <div key={index} className="col-span-full">
            <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3">
              {label}
            </Text>
          </div>
        );

      case "MultipleAutocomplete":
        const multiOptions = Array.isArray(options)
          ? options
          : fetchedOptions?.[options] || [];

        const selectedValueIds = (inputStates?.[stateKey] || "")
          ?.split(",") // from string "1,2" → array ["1", "2"]
          ?.map((id) => id.trim())
          ?.filter(Boolean);

        const selectedValues = multiOptions.filter((item) =>
          selectedValueIds?.includes(String(item[valueKey])),
        );

        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              multiple
              size="small"
              limitTags={1}
              id={`multi-autocomplete-${stateKey}-${index}`}
              options={multiOptions}
              getOptionLabel={(option) =>
                option[getOptionLabelKey] || option[valueKey] || ""
              }
              value={selectedValues}
              onChange={(event, newValue) => {
                const selectedIds = newValue.map((item) => item[valueKey]);
                // Convert array to comma-separated string like "1,2"
                updateInputObject(stateKey, selectedIds.join(","));
              }}
              renderInput={(params) => (
                <TextField {...params} fullWidth label={label} />
              )}
              disabled={disabled}
            />
          </div>
        );
      case "Textarea":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`textarea-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => updateInputObject(stateKey, e.target.value)}
              disabled={disabled}
              multiline
              minRows={1}
              maxRows={10}
            />
          </div>
        );

      case "DatePicker":
        const today = new Date();

        const maxDateOfBirth =
          stateKey === "dateOfBirth"
            ? new Date(today.setFullYear(today.getFullYear() - 18))
            : disableFutureDates
              ? new Date()
              : undefined;

        const defaultValue =
          stateKey === "applyLeaveDate"
            ? new Date()
            : selectedValue
              ? new Date(selectedValue)
              : "";

        return (
          <div key={index} className="w-full">
            <DateInput
              id="from-date"
              label={label}
              value={defaultValue}
              onChange={(date) => {
                const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
                updateInputObject(stateKey, formattedDate);
              }}
              disabled={disabled}
              maxDate={maxDateOfBirth}
              minDate={
                disablePastDates
                  ? minimumDate
                    ? new Date(minimumDate)
                    : today
                  : undefined
              }
            />
          </div>
        );

      case "Checkbox":
        return (
          <div key={index} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  id={`checkbox-${stateKey}-${index}`}
                  checked={Boolean(selectedValue)}
                  onChange={(e) =>
                    updateInputObject(stateKey, e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label={label}
            />
          </div>
        );

      case "ImageUpload":
        return (
          <div key={index} className="w-full">
            <UploadButton
              label={label}
              tooltipLabel={tooltipLabel}
              downloadFile={downloadFile}
              onChange={(e) => handleFileUpload12(e, stateKey, fileType)}
              outline={false}
              fileUrl={selectedValue}
              showFile={true}
              onRemove={() => updateInputObject(stateKey, null)}
            />
          </div>
        );

      case "TextFieldClientIntials":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`text-field-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value
                  .toUpperCase() // Ensure it's uppercase
                  .replace(/[^A-Z]/g, ""); // Remove anything that isn't a letter
                updateInputObject(stateKey, val);
              }}
              disabled={disabled}
            />
          </div>
        );

      case "emailId":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`email-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value;
                updateInputObject(stateKey, val);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const errorMessage =
                  val && !emailRegex.test(val) ? "Invalid email format" : "";
                setInputErrors((prev) => ({
                  ...prev,
                  [stateKey]: errorMessage,
                }));
              }}
              error={Boolean(inputErrors[stateKey])}
              helperText={inputErrors[stateKey] || ""}
              disabled={disabled}
            />
          </div>
        );

      case "phoneNo":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`phone-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, "");
                updateInputObject(stateKey, onlyDigits);
                const errorMessage =
                  onlyDigits && onlyDigits.length !== 10
                    ? "Phone number must be exactly 10 digits"
                    : "";
                setInputErrors((prev) => ({
                  ...prev,
                  [stateKey]: errorMessage,
                }));
              }}
              error={Boolean(inputErrors[stateKey])}
              helperText={inputErrors[stateKey] || ""}
              disabled={disabled}
            />
          </div>
        );

      case "UnitModal":
        return (
          <div key={index} className="w-full">
            <MultiValueInputModal
              buttonLabel={label}
              stateKey={stateKey}
              inputStates={inputStates}
              updateInputObject={updateInputObject}
              groupId={fieldConfig.groupId}
            />
          </div>
        );

      case "emptySpace":
        return <div key={index} className="w-full" />;

      default:
        return null; // Handle other types if needed
    }
  };

  return (
    <div
      className={`grid gap-4 ${
        colSpan?.length
          ? `grid-cols-${colSpan[0]} sm:grid-cols-${colSpan[1]} md:grid-cols-${colSpan[2]} lg:grid-cols-${colSpan[3]}`
          : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      }`}
    >
      {inputFields.map(renderInputField)}
    </div>
  );
};

export default CommonForm;
